import axios, { AxiosInstance } from 'axios';
import { format, isValid, parseISO } from 'date-fns';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';
import type { AnalyticsSignalsPayload } from './analytics';

// ── Billing Dashboard ─────────────────────────────────────────────────

export type BillingPlanStat = { label: string; allowance: string; used: string };
export type BillingUsageLine = { component: string; usage: string; rate: string; amount_cents: number };
export type BillingPaymentMethod = { brand: string; last4: string; exp_month: number; exp_year: number };
export type BillingDashboardData = {
  plan_name: string;
  plan_price_str: string;
  plan_stats: BillingPlanStat[];
  usage_lines: BillingUsageLine[];
  total_amount_cents: number;
  payment_method: BillingPaymentMethod | null;
  pricing: BillingPricingData | null;
};

function mapRealBillingDashboard(usage: any, pricing: BillingPricingData | null): BillingDashboardData {
  // ── Plan header ──
  const planName = usage?.plan_name ?? usage?.plan ?? 'Workspace';
  const baseCents: number = usage?.plan_price_cents ?? usage?.base_fee_cents ?? 0;
  const renewsAt: string = usage?.period_end ?? usage?.renews_at ?? '';
  const priceStr = [
    baseCents > 0 ? `$${(baseCents / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}/month` : '',
    renewsAt ? `Renews ${renewsAt}` : '',
  ].filter(Boolean).join(' · ');

  // ── Plan stats (only include rows the API actually returns) ──
  const planStats: BillingPlanStat[] = [];
  if (usage?.voice_minutes_used != null || usage?.voice_minutes_limit != null) {
    planStats.push({
      label: 'Voice minutes',
      allowance: usage?.voice_minutes_limit != null ? `${Number(usage.voice_minutes_limit).toLocaleString()} / mo` : 'Unlimited',
      used: `Used: ${usage?.voice_minutes_used != null ? Number(usage.voice_minutes_used).toLocaleString() : '—'}`,
    });
  }
  if (usage?.session_count != null) {
    planStats.push({
      label: 'Voice sessions',
      allowance: usage?.session_limit != null ? `${Number(usage.session_limit).toLocaleString()} / mo` : 'Unlimited',
      used: `Used: ${Number(usage.session_count).toLocaleString()}`,
    });
  }
  if (usage?.sms_count != null || usage?.sms_limit != null) {
    planStats.push({
      label: 'SMS',
      allowance: usage?.sms_limit != null ? `${Number(usage.sms_limit).toLocaleString()} / mo` : 'Unlimited',
      used: `Used: ${usage?.sms_count != null ? Number(usage.sms_count).toLocaleString() : '—'}`,
    });
  }
  if (usage?.agent_count != null || usage?.agent_limit != null) {
    planStats.push({
      label: 'Agents',
      allowance: usage?.agent_limit != null ? `${Number(usage.agent_limit).toLocaleString()} / mo` : 'Unlimited',
      used: `${usage?.agent_count != null ? Number(usage.agent_count).toLocaleString() : '—'} created`,
    });
  }

  // ── Usage lines: join pricing rates with usage counts ──
  const pricingMap: Record<string, BillingPricingEvent> = {};
  if (pricing?.events) {
    for (const ev of pricing.events) {
      pricingMap[ev.name] = ev;
    }
  }

  const usageLines: BillingUsageLine[] = [];

  if (baseCents > 0) {
    usageLines.push({ component: 'Platform fee', usage: '–', rate: `$${(baseCents / 100).toFixed(2)}/mo`, amount_cents: baseCents });
  }

  const eventMap: Array<{ key: string; label: string; countKey: string; unit: string }> = [
    { key: 'session_start',       label: 'Session starts',       countKey: 'session_count',      unit: 'sessions' },
    { key: 'per_turn',            label: 'Voice turns',          countKey: 'turn_count',         unit: 'turns' },
    { key: 'rag_retrieval',       label: 'Knowledge lookups',    countKey: 'rag_count',          unit: 'lookups' },
    { key: 'appointment_booked',  label: 'Bookings',             countKey: 'booking_count',      unit: 'bookings' },
    { key: 'callback_created',    label: 'Callbacks',            countKey: 'callback_count',     unit: 'callbacks' },
    { key: 'warm_transfer',       label: 'Warm transfers',       countKey: 'transfer_count',     unit: 'transfers' },
    { key: 'sms_sent',            label: 'SMS',                  countKey: 'sms_count',          unit: 'messages' },
    { key: 'email_sent',          label: 'Email',                countKey: 'email_count',        unit: 'emails' },
    { key: 'url_push',            label: 'URL pushes',           countKey: 'push_count',         unit: 'pushes' },
    { key: 'crm_write',           label: 'CRM writes',           countKey: 'crm_write_count',    unit: 'writes' },
    { key: 'signalstack_event',   label: 'SignalStack events',   countKey: 'signal_count',       unit: 'events' },
  ];

  for (const { key, label, countKey, unit } of eventMap) {
    const ev = pricingMap[key];
    if (!ev) continue;
    
    // Use granular event data if available, fallback to legacy top-level keys
    const usageData = usage?.by_event_type?.[key];
    const count = usageData?.count ?? (usage?.[countKey] != null ? Number(usage[countKey]) : null);
    
    // Use explicit cents from usage record if present, otherwise calculate from count * rate
    const amountCents = usageData?.usd_cents ?? (count != null && ev.usd_cents > 0 ? Math.round(count * ev.usd_cents) : 0);

    usageLines.push({
      component: label,
      usage: count != null ? `${count.toLocaleString()} ${unit}` : '–',
      rate: ev.usd_cents > 0 ? `$${(ev.usd_cents / 100).toFixed(2)} each` : `${ev.credits} credits`,
      amount_cents: amountCents,
    });
  }

  const totalCents: number = usage?.total_usd_cents != null
    ? Number(usage.total_usd_cents)
    : usageLines.reduce((s, l) => s + l.amount_cents, 0);

  return {
    plan_name: planName,
    plan_price_str: priceStr,
    plan_stats: planStats,
    usage_lines: usageLines,
    total_amount_cents: totalCents,
    payment_method: usage?.payment_method ?? null,
    pricing,
  };
}

export async function fetchBillingDashboard(axiosInstance?: AxiosInstance, params?: { days?: number }): Promise<BillingDashboardData> {
  const client = axiosInstance || getVoiceFallbackClient();
  const [usageRes, pricingRes] = await Promise.allSettled([
    client.get<any>(`${apiConfig.voiceEndpoints.billing}/usage`, params ? { params } : undefined),
    client.get<any>(`${apiConfig.voiceEndpoints.billing}/pricing`),
  ]);
  const usage = usageRes.status === 'fulfilled' ? usageRes.value.data : null;
  const pricingRaw = pricingRes.status === 'fulfilled' ? pricingRes.value.data : null;
  
  // Reuse fetchBillingPricing logic to normalize the object
  const rate = Number(pricingRaw?.rate_usd_per_credit ?? 0);
  const eventsObj: Record<string, any> = (pricingRaw?.events && typeof pricingRaw.events === 'object') ? pricingRaw.events : {};
  const events: BillingPricingEvent[] = Object.entries(eventsObj).map(([key, val]) => ({
    name: key,
    credits: Number((val as any)?.credits ?? 0),
    usd_cents: Number((val as any)?.usd_cents ?? 0),
  }));
  const pricing: BillingPricingData = { rate_usd_per_credit: rate, events };

  return mapRealBillingDashboard(usage, pricing);
}

/** `GET /api/voice/sessions/{id}/report?format=html` — download server-generated post-call report. */
export async function downloadSessionReport(
  sessionId: string,
  format: 'html' | 'pdf' = 'html',
  axiosInstance?: AxiosInstance
): Promise<Blob> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<Blob>(
    `${apiConfig.voiceEndpoints.voice}/sessions/${sessionId}/report`,
    { params: { format }, responseType: 'blob' }
  );
  return response.data;
}

/** `GET /api/analytics/signals?session_id={session_id}` — aggregate signal analytics for a session. */
export async function fetchSessionSignals(sessionId: string, axiosInstance?: AxiosInstance): Promise<AnalyticsSignalsPayload | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.analytics}/signals`, { params: { session_id: sessionId } });
  return response.data ?? null;
}

export type BillingSessionEvent = {
  id: string;
  session_id: string;
  campaign_id: string;
  event_type: string;
  credits: number;
  usd_cents: number;
  meta?: Record<string, unknown>;
  occurred_at: string;
};

/** `GET /api/billing/sessions/{session_id}/events` — billing events for a session. */
export async function fetchSessionBillingEvents(sessionId: string, axiosInstance?: AxiosInstance): Promise<BillingSessionEvent[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.billing}/sessions/${sessionId}/events`);
  return Array.isArray(response.data) ? response.data : response.data?.events ?? [];
}

// ── Billing extras ────────────────────────────────────────────────────

export type BillingPricingTier = { name: string; price_cents: number; features: string[] };
export type BillingPricingEvent = { name: string; credits: number; usd_cents: number };
export type BillingPricingData = { rate_usd_per_credit: number; events: BillingPricingEvent[] };

/** Customer-facing success fee schedule per tier. Events with usd_cents=0 are excluded. */
export type BillingTierPricingData = {
  tier: string;
  /** Keyed by event_type, value is usd_cents (integer). Only billable (>0) events included. */
  events: Record<string, number>;
};

/** `GET /api/billing/usage` — customer-facing aggregate usage for the workspace. */
export type BillingUsageEventStat = {
  count: number;
  usd_cents: number;
};

export type BillingUsageData = {
  workspace_id: string;
  period_start: string;
  period_end: string;
  total_usd_cents: number;
  session_count: number;
  by_event_type: Record<string, BillingUsageEventStat>;
};

/** `GET /api/billing/pricing?tier={tier}` — success fee schedule for a tier (customer-facing). */
export async function fetchBillingPricingByTier(tier?: string, axiosInstance?: AxiosInstance): Promise<BillingTierPricingData | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.billing}/pricing${tier ? `?tier=${encodeURIComponent(tier)}` : ''}`;
  const response = await client.get<any>(url);
  const d = response.data;
  if (!d) return null;
  const eventsObj: Record<string, any> = (d.events && typeof d.events === 'object') ? d.events : {};
  const events: Record<string, number> = {};
  for (const [key, val] of Object.entries(eventsObj)) {
    const cents = Number((val as any)?.usd_cents ?? val ?? 0);
    if (cents > 0) events[key] = cents;
  }
  return { tier: String(d.tier ?? tier ?? 'scale'), events };
}

/** `GET /api/billing/usage?days={days}` — aggregated workspace usage (customer-facing, no credits). */
export async function fetchBillingUsage(days?: number, axiosInstance?: AxiosInstance): Promise<BillingUsageData | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const params = days != null ? { days } : undefined;
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.billing}/usage`, params ? { params } : undefined);
  const d = response.data;
  if (!d) return null;
  const byEvent: Record<string, BillingUsageEventStat> = {};
  if (d.by_event_type && typeof d.by_event_type === 'object') {
    for (const [key, val] of Object.entries(d.by_event_type as Record<string, any>)) {
      byEvent[key] = { count: Number(val?.count ?? 0), usd_cents: Number(val?.usd_cents ?? 0) };
    }
  }
  return {
    workspace_id: String(d.workspace_id ?? ''),
    period_start: String(d.period_start ?? ''),
    period_end: String(d.period_end ?? ''),
    total_usd_cents: Number(d.total_usd_cents ?? 0),
    session_count: Number(d.session_count ?? 0),
    by_event_type: byEvent,
  };
}

/** `GET /api/billing/pricing` — public pricing reference (legacy, uses internal credit model). */
export async function fetchBillingPricing(axiosInstance?: AxiosInstance): Promise<BillingPricingData | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.billing}/pricing`);
  const d = response.data;
  if (!d) return null;
  const rate = Number(d.rate_usd_per_credit ?? 0);
  const eventsObj: Record<string, any> = (d.events && typeof d.events === 'object') ? d.events : {};
  const events: BillingPricingEvent[] = Object.entries(eventsObj).map(([key, val]) => ({
    name: key,
    credits: Number((val as any)?.credits ?? 0),
    usd_cents: Number((val as any)?.usd_cents ?? 0),
  }));
  return { rate_usd_per_credit: rate, events };
}
